import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import GameHeader from './GameHeader';
import UpgradeButton from './UpgradeButton';

// Helper to abbreviate basketball positions
const abbreviatePosition = (position) => {
  if (!position) return null;
  const pos = typeof position === 'object' ? (position.abbreviation || position.name) : position;
  if (!pos) return null;

  const abbrevMap = {
    'Point Guard': 'PG',
    'Shooting Guard': 'SG',
    'Small Forward': 'SF',
    'Power Forward': 'PF',
    'Center': 'C',
    'Forward': 'F',
    'Guard': 'G',
    'Guard-Forward': 'G-F',
    'Forward-Guard': 'F-G',
    'Forward-Center': 'F-C',
    'Center-Forward': 'C-F'
  };

  return abbrevMap[pos] || pos;
};

// Helper to check if a player is in the starting lineup
const isPlayerInLineup = (playerName, playerTeam, game, playerId) => {
  if (!game) return false;

  // Helper to check if a lineup entry matches by playerId or last name
  const matchesPlayer = (entry) => {
    if (!entry) return false;
    if (typeof entry === 'object' && entry.playerId && playerId) {
      return String(entry.playerId) === String(playerId);
    }
    const lineupName = typeof entry === 'object' ? entry.name : entry;
    if (!lineupName || typeof lineupName !== 'string' || !playerName) return false;
    const nameParts = playerName.trim().split(' ');
    const playerLastName = nameParts[nameParts.length - 1].toLowerCase();
    const lineupLastName = lineupName.trim().split(' ').pop().toLowerCase();
    return lineupLastName === playerLastName;
  };

  const checkLineup = (lineup) => {
    if (!lineup) return false;
    if (typeof lineup === 'object' && !Array.isArray(lineup)) {
      return Object.values(lineup).some(matchesPlayer);
    }
    if (Array.isArray(lineup)) {
      return lineup.some(matchesPlayer);
    }
    return false;
  };

  // Try team-based lookup first
  const isAway = playerTeam === game.away || (playerTeam && playerTeam === game.awayTeamId);
  const isHome = playerTeam === game.home || (playerTeam && playerTeam === game.homeTeamId);
  if (isAway) return checkLineup(game.awayLineup);
  if (isHome) return checkLineup(game.homeLineup);

  // Team didn't match — check both lineups (playerIds are unique)
  return checkLineup(game.awayLineup) || checkLineup(game.homeLineup);
};

// Helper to get player news/injury status
// Checks game.news first (keyed by playerId), falls back to injury arrays
const getPlayerInjury = (playerId, playerName, playerTeam, game) => {
  if (!game) return null;
  // Check news object first (keyed by playerId)
  if (game.news && playerId && game.news[String(playerId)]) {
    const entry = game.news[String(playerId)];
    const latest = entry.updates && entry.updates[0];
    if (latest) {
      const s = (latest.status || '').toLowerCase();
      return {
        status: latest.status,
        details: latest.bodyPart,
        type: s.includes('out') ? 'out' : s.includes('questionable') ? 'questionable' : s.includes('doubtful') ? 'doubtful' : s
      };
    }
  }
  // Fall back to injuries arrays
  const isAway = playerTeam === game.away || (playerTeam && playerTeam === game.awayTeamId);
  const isHome = playerTeam === game.home || (playerTeam && playerTeam === game.homeTeamId);
  const injuries = isAway ? game.awayInjuries : isHome ? game.homeInjuries : null;
  if (!injuries || !injuries.length) return null;
  return injuries.find(inj =>
    (inj.playerId && playerId && String(inj.playerId) === String(playerId)) ||
    inj.playerName === playerName
  ) || null;
};

// Maps injury type to badge background color
const getInjuryColor = (type) => {
  switch (type) {
    case 'out': return '#dc3545';
    case 'questionable': return '#000000';
    case 'doubtful': return '#6c757d';
    default: return '#0d6efd';
  }
};

// Position badge component with injury-aware styling (mobile)
// Out=red, Questionable=yellow, Doubtful=grey, other=blue. Tap for tooltip.
const PositionBadge = ({ player, game }) => {
  const [showTip, setShowTip] = useState(false);
  const tipRef = useRef(null);

  useEffect(() => {
    if (!showTip) return;
    const dismiss = (e) => {
      if (tipRef.current && !tipRef.current.contains(e.target)) setShowTip(false);
    };
    document.addEventListener('touchstart', dismiss);
    const timer = setTimeout(() => setShowTip(false), 3000);
    return () => { document.removeEventListener('touchstart', dismiss); clearTimeout(timer); };
  }, [showTip]);

  const pos = abbreviatePosition(player.positionCode || player.position);
  if (!pos) return null;
  const injury = getPlayerInjury(player.playerId, player.name, player.team || player.teamId, game);
  if (injury) {
    const detail = `${injury.status}${injury.details ? ' - ' + injury.details : ''}`;
    const bg = getInjuryColor(injury.type);
    const inLineup = isPlayerInLineup(player.name, player.team || player.teamId, game, player.playerId);
    return (
      <span ref={tipRef} style={{ position: 'relative', display: 'inline-flex', marginLeft: '3px', flexShrink: 0 }}>
        <span
          onClick={(e) => { e.stopPropagation(); setShowTip(prev => !prev); }}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: '20px', height: '14px', borderRadius: '7px',
            background: bg, color: inLineup ? '#ffc107' : 'white',
            fontSize: '0.6em', fontWeight: 'bold',
            padding: '0 4px', cursor: 'pointer'
          }}
        >{pos}</span>
        {showTip && (
          <span style={{
            position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
            marginBottom: '4px', padding: '3px 8px', borderRadius: '4px',
            background: '#1f2937', color: 'white', fontSize: '0.65rem',
            whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none'
          }}>{detail}</span>
        )}
      </span>
    );
  }
  const inLineup = isPlayerInLineup(player.name, player.team || player.teamId, game, player.playerId);
  return (
    <span style={{
      fontSize: '0.7em',
      color: inLineup ? '#ffc107' : '#6c757d',
      fontWeight: inLineup ? 'bold' : 'normal',
      marginLeft: '3px'
    }}>({pos})</span>
  );
};

const MobileBasketballDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  onPlayerClick,
  onPlayerContextMenu,
  onOddsClick,
  onOddsContextMenu,
  clickedCellId,
  showTiers,
  onTierToggle,
  masterTierFilter,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick,
  nbaViewMode = 'ou' // 'ou' for O/U view, 'bestline' for Best Line view
}) => {
  // Debug logging
  console.log('MobileBasketballDataTable props:', { nbaViewMode, gameSport: game?.sport });

  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'wnba', ...game };

  const handleNameContextMenu = (player) => (e) => {
    if (!onPlayerContextMenu) return;
    const teamAbbr =
      player?.teamAbv ||
      (player?.team === 'away' ? safeGame.away : player?.team === 'home' ? safeGame.home : player?.team) ||
      player?.teamId ||
      null;
    onPlayerContextMenu(e, safeGame.gameIDmlb, player?.playerId, player?.name, teamAbbr);
  };

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Safety check - if selectedMarket is undefined, show loading (AFTER all hooks)
  if (!selectedMarket) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      // Market just changed, set rendering state synchronously
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Get visible books for mobile
  const visibleBookmakers = allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage);

  // Format the live status
  const formatBasketballLiveStatus = (status) => {
    if (!status || status === 'Scheduled') return '';

    // Check for end game states (case-insensitive)
    const upperStatus = status.toUpperCase();
    if (status === 'InProgress') return 'Live';
    if (status === 'Final' || status === 'Completed') return 'Final';
    if (upperStatus === 'STATUS_FINAL' || upperStatus.includes('FINAL')) return 'Final';
    if (status === 'Final/OT') return 'Final/OT';
    if (status === 'Postponed') return 'Postponed';

    // Handle quarter/half format
    if (status.includes('Q') || status.includes('Half')) {
      return status;
    }

    // Return as-is for any other status
    return status;
  };

  // Tier color styling function
  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    if (price === bestBookPrice && count > 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue >= 0.25) {
        return 'Best odds - Tier 1 edge';
      } else if (diffValue >= 0.15) {
        return 'Best odds - Tier 2 edge';
      } else if (diffValue >= 0.05) {
        return 'Best odds - Tier 3 edge';
      }
      return 'Best odds';
    }
    return '';
  };

  // Tier color styling function
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count) => {
    const style = {
      fontWeight: 'bold',
      fontSize: '0.8rem'
    };

    const occurrences = [price].filter(p => p === bestBookPrice).length;

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {
      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, {
            backgroundColor: 'green',
            color: 'yellow',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text for better contrast
          Object.assign(style, {
            backgroundColor: '#FFC107',
            color: '#212529',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, {
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        }
      }
    }

    return style;
  };

  // Calculate best line for a player/book combo based on edge (for Best Line view)
  // Takes all lines for a player and finds the one with best edge for the specified book
  const getBestLineByEdge = (allPlayerLines, book, market) => {
    let bestEdgeLine = null;
    let bestEdgeDiff = -Infinity;
    let highestPointLine = null;
    let highestPoint = -Infinity;

    // Iterate through all lines (different point values) for this player
    allPlayerLines.forEach(line => {
      const overOdds = line[`${market}_${book}_over`];
      const underOdds = line[`${market}_${book}_under`];
      const bookPoint = line[`${market}_${book}_point`] || line.point;
      const bestBookPrice = line[`${market}_bestBookPrice`];
      const bestBookDiff = line[`${market}_bestBookDiff`];
      const isAlternate = line.isAlternate;

      // Skip if this book has no odds for this line
      if ((overOdds === null || overOdds === undefined) && (underOdds === null || underOdds === undefined)) {
        return;
      }

      // Track highest point non-alternate line as fallback (only if book has odds for it)
      // Skip lines where both over and under are -200 or worse
      const hasValidOver = overOdds !== null && overOdds !== undefined && overOdds > -200;
      const hasValidUnder = underOdds !== null && underOdds !== undefined && underOdds > -200;

      if (!isAlternate && bookPoint !== null && bookPoint !== undefined && bookPoint > highestPoint && (hasValidOver || hasValidUnder)) {
        highestPoint = bookPoint;
        highestPointLine = {
          line,
          overOdds,
          underOdds,
          bookPoint,
          bestBookPrice,
          bestBookDiff
        };
      }

      // Check if over has an edge (and book has odds for it)
      // Skip odds -200 or worse in edge view
      if (overOdds !== null && overOdds !== undefined && overOdds > -200 && overOdds === bestBookPrice && bestBookDiff > bestEdgeDiff) {
        bestEdgeDiff = bestBookDiff;
        bestEdgeLine = {
          odds: overOdds,
          point: bookPoint,
          side: 'over',
          bestBookPrice,
          bestBookDiff,
          isAvailable: line[`${market}_${book}_over_isAvailable`] !== false
        };
      }

      // Check if under has an edge (and book has odds for it)
      // Skip odds -200 or worse in edge view
      if (underOdds !== null && underOdds !== undefined && underOdds > -200 && underOdds === bestBookPrice && bestBookDiff > bestEdgeDiff) {
        bestEdgeDiff = bestBookDiff;
        bestEdgeLine = {
          odds: underOdds,
          point: bookPoint,
          side: 'under',
          bestBookPrice,
          bestBookDiff,
          isAvailable: line[`${market}_${book}_under_isAvailable`] !== false
        };
      }
    });

    // If we found a line with edge, return it
    if (bestEdgeLine) {
      return bestEdgeLine;
    }

    // Otherwise, return the highest point non-alternate line (only if it has odds)
    if (highestPointLine) {
      const { overOdds, underOdds, bookPoint, bestBookPrice, bestBookDiff } = highestPointLine;

      // Choose over or under based on better odds (skip odds -200 or worse)
      const validOver = overOdds !== null && overOdds !== undefined && overOdds > -200;
      const validUnder = underOdds !== null && underOdds !== undefined && underOdds > -200;

      if (validOver && validUnder) {
        if (overOdds >= underOdds) {
          return {
            odds: overOdds,
            point: bookPoint,
            side: 'over',
            bestBookPrice,
            bestBookDiff,
            isAvailable: highestPointLine.line[`${market}_${book}_over_isAvailable`] !== false
          };
        } else {
          return {
            odds: underOdds,
            point: bookPoint,
            side: 'under',
            bestBookPrice,
            bestBookDiff,
            isAvailable: highestPointLine.line[`${market}_${book}_under_isAvailable`] !== false
          };
        }
      } else if (validOver) {
        return {
          odds: overOdds,
          point: bookPoint,
          side: 'over',
          bestBookPrice,
          bestBookDiff,
          isAvailable: highestPointLine.line[`${market}_${book}_over_isAvailable`] !== false
        };
      } else if (validUnder) {
        return {
          odds: underOdds,
          point: bookPoint,
          side: 'under',
          bestBookPrice,
          bestBookDiff,
          isAvailable: highestPointLine.line[`${market}_${book}_under_isAvailable`] !== false
        };
      }
    }

    // No valid line found for this book
    return null;
  };

  // Handle empty or loading data
  if (!displayData || displayData.length === 0 || showLoading) {
    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameDate={gameDate}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={true}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatBasketballLiveStatus}
          showScores={true}
            isLoading={showLoading}
        />

        {!isCollapsed && (
          <div className="table-container">
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td colSpan={2} className="text-center" style={{ padding: '20px', color: '#999' }}>
                    {isLoading ? 'Loading odds...' : (
                      (game?.gameIDmlb?.startsWith('locked_') || game?.gameId?.startsWith('locked_')) ? (
                        <UpgradeButton onClick={onSettingsClick} />
                      ) : (
                        'No odds available'
                      )
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  }

  // Process data
  let filteredData = [...displayData];

  // Filter based on showTiers setting and nbaViewMode
  // IMPORTANT: In Best Line mode, we need ALL alternates to find the best edge, so skip alternate filtering
  if (masterTierFilter !== null) {
    filteredData = filteredData.filter(player => {
      const playerTier = parseFloat(player.point);
      return playerTier === masterTierFilter;
    });
  } else {
    // Standard tier filtering logic for basketball
    // Only filter out alternates if there are non-alternate lines available
    // IMPORTANT: In Best Line mode, we need ALL alternates to find the best edge, so skip alternate filtering
    const hasNonAlternates = displayData.some(player => !player.isAlternate);
    filteredData = (showTiers || nbaViewMode === 'bestline') ? filteredData : (hasNonAlternates ? filteredData.filter(player => !player.isAlternate) : filteredData);
  }

  // Sort the filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    // First by team
    if (a.team !== b.team) {
      if (a.team === safeGame.away) return -1;
      if (b.team === safeGame.away) return 1;
    }

    // Then by starter status (starters first)
    if (a.starter !== b.starter) {
      return a.starter ? -1 : 1;
    }

    // Then by name
    return (a.name || '').localeCompare(b.name || '');
  });

  // Handle h2h data for ML market
  let h2hData = [];
  let totalsData = [];
  let spreadsData = [];

  if (selectedMarket === 'h2h') {
    let allTotals = [];
    let allSpreads = [];

    filteredData.forEach(player => {
      if (player.playerId === 'team_away' || player.playerId === 'team_home') {
        h2hData.push(player);
      } else if (player.playerId?.startsWith('totals_')) {
        allTotals.push(player);
      } else if (player.playerId?.startsWith('spreads_')) {
        allSpreads.push(player);
      }
    });

    // Sort h2h data: away team first, then home team
    h2hData.sort((a, b) => {
      if (a.team === 'away' && b.team === 'home') return -1;
      if (a.team === 'home' && b.team === 'away') return 1;
      return 0;
    });

    // For totals: find first available over/under for each book (NHL pattern)
    // Create single over and under objects with all book odds
    const overLine = { name: 'Over', betType: 'over', playerId: 'totals_over', point: null, pointsByBook: {} };
    const underLine = { name: 'Under', betType: 'under', playerId: 'totals_under', point: null, pointsByBook: {} };

    visibleBookmakers.forEach(book => {
      // Find first over with data for this book
      const overData = allTotals.find(t =>
        t.betType === 'over' &&
        t[`totals_${book}`] !== null &&
        t[`totals_${book}`] !== undefined
      );

      if (overData) {
        overLine[`totals_${book}`] = overData[`totals_${book}`];
        overLine[`totals_${book}_direction`] = overData[`totals_${book}_direction`];
        overLine[`totals_bestBookPrice`] = overData[`totals_bestBookPrice`];
        overLine[`totals_bestBookDiff`] = overData[`totals_bestBookDiff`];
        overLine.pointsByBook[book] = overData.point;
        if (!overLine.point) overLine.point = overData.point;

        // Find matching under with same point
        const underData = allTotals.find(t =>
          t.betType === 'under' &&
          t.point === overData.point &&
          t[`totals_${book}`] !== null &&
          t[`totals_${book}`] !== undefined
        );

        if (underData) {
          underLine[`totals_${book}`] = underData[`totals_${book}`];
          underLine[`totals_${book}_direction`] = underData[`totals_${book}_direction`];
          underLine[`totals_bestBookPrice`] = underData[`totals_bestBookPrice`];
          underLine[`totals_bestBookDiff`] = underData[`totals_bestBookDiff`];
          underLine.pointsByBook[book] = underData.point;
          if (!underLine.point) underLine.point = underData.point;
        }
      }
    });

    totalsData = [overLine, underLine];

    // For spreads: find first available spread for each team (NHL pattern)
    const awaySpreadLine = { name: game?.away || 'Away', team: game?.away || 'away', playerId: 'spreads_away', point: null, pointsByBook: {} };
    const homeSpreadLine = { name: game?.home || 'Home', team: game?.home || 'home', playerId: 'spreads_home', point: null, pointsByBook: {} };

    visibleBookmakers.forEach(book => {
      // Find away spread for this book
      const awayData = allSpreads.find(s =>
        s.team === 'away' &&
        s[`spreads_${book}`] !== null &&
        s[`spreads_${book}`] !== undefined
      );

      if (awayData) {
        awaySpreadLine[`spreads_${book}`] = awayData[`spreads_${book}`];
        awaySpreadLine[`spreads_${book}_direction`] = awayData[`spreads_${book}_direction`];
        awaySpreadLine[`spreads_bestBookPrice`] = awayData[`spreads_bestBookPrice`];
        awaySpreadLine[`spreads_bestBookDiff`] = awayData[`spreads_bestBookDiff`];
        awaySpreadLine.pointsByBook[book] = awayData.point;
        if (!awaySpreadLine.point) awaySpreadLine.point = awayData.point;
      }

      // Find home spread for this book
      const homeData = allSpreads.find(s =>
        s.team === 'home' &&
        s[`spreads_${book}`] !== null &&
        s[`spreads_${book}`] !== undefined
      );

      if (homeData) {
        homeSpreadLine[`spreads_${book}`] = homeData[`spreads_${book}`];
        homeSpreadLine[`spreads_${book}_direction`] = homeData[`spreads_${book}_direction`];
        homeSpreadLine[`spreads_bestBookPrice`] = homeData[`spreads_bestBookPrice`];
        homeSpreadLine[`spreads_bestBookDiff`] = homeData[`spreads_bestBookDiff`];
        homeSpreadLine.pointsByBook[book] = homeData.point;
        if (!homeSpreadLine.point) homeSpreadLine.point = homeData.point;
      }
    });

    spreadsData = [awaySpreadLine, homeSpreadLine];

    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameDate={gameDate}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={true}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatBasketballLiveStatus}
          showScores={true}
            isLoading={showLoading}
        />

        {!isCollapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Moneyline Table */}
            {h2hData.length > 0 && (
              <div className="table-container">
                <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'rgb(245, 158, 11)' }}>
                  Moneyline
                </h6>
                <Table bordered size="sm" className="text-center">
                  <thead>
                    <tr>
                      <th style={{ width: '120px' }}></th>
                      {visibleBookmakers.map(book => {
                        const bookLink = game?.sgoLinks?.bookmakers?.[book];
                        return (
                          <th key={`h2h-${book}`} style={{ fontSize: '0.75rem' }}>
                            {bookLink ? (
                              <a
                                href={bookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  touchAction: 'auto',
                                  WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                  padding: '4px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={getBookLogoUrl(book)}
                                  alt={`${book} logo`}
                                  title={book}
                                  style={{
                                    height: '16px',
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </a>
                            ) : (
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '16px' }}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {h2hData.map((team, index) => (
                      <tr key={`h2h-${index}`}>
                        <td style={{ width: '120px', fontWeight: 'bold', padding: '8px 4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <img
                              src={team.team === 'away' ? safeGame.awayLogo : safeGame.homeLogo}
                              alt={team.team === 'away' ? safeGame.away : safeGame.home}
                              style={{ width: '20px', height: '20px' }}
                              onError={(e) => {
                                e.target.style.visibility = 'hidden';
                                e.target.style.width = '0px';
                                e.target.style.height = '0px';
                                e.target.style.margin = '0px';
                              }}
                            />
                            <span>
                              {team.team === 'away'
                                ? (safeGame.awayNickname || safeGame.away)
                                : (safeGame.homeNickname || safeGame.home)
                              }
                            </span>
                          </div>
                        </td>
                        {visibleBookmakers.map(book => {
                          const odds = team[`h2h_${book}`];
                          return (
                            <td
                              key={`h2h-${book}-${index}`}
                              className={getCellClassName(safeGame.gameIDmlb, team.playerId, 'h2h', book, null, 'moneyline')}
                              style={{ textAlign: 'center', padding: '8px 4px', cursor: odds ? 'pointer' : 'default' }}
                              onClick={() => {
                                if (odds && onOddsClick) {
                                  onOddsClick(safeGame.gameIDmlb, team.playerId, team.name, 'h2h', book, odds, safeGame.away, safeGame.home, team.team, null, null, 'moneyline', team.team === safeGame.home ? safeGame.homeTeamId : safeGame.awayTeamId);
                                }
                              }}
                              onContextMenu={(e) => {
                                if (odds && onOddsContextMenu) {
                                  onOddsContextMenu(e, safeGame.gameIDmlb, team.playerId, team.name, 'h2h', book, odds, safeGame.away, safeGame.home, team.team, null, null, 'moneyline');
                                }
                              }}
                            >
                              {odds && (
                                <span style={{ fontWeight: 'bold' }}>
                                  {odds > 0 ? `+${odds}` : odds}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Totals Table */}
            {totalsData.length > 0 && (
              <div className="table-container">
                <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'rgb(245, 158, 11)' }}>
                  Totals
                </h6>
                <Table bordered size="sm" className="text-center">
                  <thead>
                    <tr>
                      <th style={{ width: '120px' }}></th>
                      {visibleBookmakers.map(book => {
                        const bookLink = game?.sgoLinks?.bookmakers?.[book];
                        return (
                          <th key={`totals-${book}`} style={{ fontSize: '0.75rem' }}>
                            {bookLink ? (
                              <a
                                href={bookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  touchAction: 'auto',
                                  WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                  padding: '4px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={getBookLogoUrl(book)}
                                  alt={`${book} logo`}
                                  title={book}
                                  style={{
                                    height: '16px',
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </a>
                            ) : (
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '16px' }}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {totalsData.map((total, index) => (
                      <tr key={`totals-${index}`}>
                        <td style={{ width: '120px', fontWeight: 'bold', padding: '8px 4px' }}>
                          {total.name}
                        </td>
                        {visibleBookmakers.map(book => {
                          const odds = total[`totals_${book}`];
                          const point = total.pointsByBook?.[book] || total.point;
                          const isOver = total.betType === 'over';
                          return (
                            <td
                              key={`totals-${book}-${index}`}
                              className={getCellClassName(safeGame.gameIDmlb, total.playerId, 'totals', book, point, total.betType)}
                              style={{ textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem', cursor: odds ? 'pointer' : 'default' }}
                              onClick={() => {
                                if (odds && onOddsClick) {
                                  onOddsClick(safeGame.gameIDmlb, total.playerId, total.name, 'totals', book, odds, safeGame.away, safeGame.home, total.team || total.name, null, point, total.betType);
                                }
                              }}
                              onContextMenu={(e) => {
                                if (odds && onOddsContextMenu) {
                                  onOddsContextMenu(e, safeGame.gameIDmlb, total.playerId, total.name, 'totals', book, odds, safeGame.away, safeGame.home, total.team || total.name, null, point, total.betType);
                                }
                              }}
                            >
                              {odds ? (
                                <div>
                                  {isOver ? (
                                    <>
                                      <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>o{point}</div>
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>u{point}</div>
                                      <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                    </>
                                  )}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Spreads Table */}
            {spreadsData.length > 0 && (
              <div className="table-container">
                <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'rgb(245, 158, 11)' }}>
                  Spreads
                </h6>
                <Table bordered size="sm" className="text-center">
                  <thead>
                    <tr>
                      <th style={{ width: '120px' }}></th>
                      {visibleBookmakers.map(book => {
                        const bookLink = game?.sgoLinks?.bookmakers?.[book];
                        return (
                          <th key={`spreads-${book}`} style={{ fontSize: '0.75rem' }}>
                            {bookLink ? (
                              <a
                                href={bookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  touchAction: 'auto',
                                  WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                  padding: '4px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={getBookLogoUrl(book)}
                                  alt={`${book} logo`}
                                  title={book}
                                  style={{
                                    height: '16px',
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </a>
                            ) : (
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{ height: '16px' }}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {spreadsData.map((spread, index) => {
                      const isAway = spread.playerId === 'spreads_away';
                      return (
                        <tr key={`spreads-${index}`}>
                          <td style={{ width: '120px', fontWeight: 'bold', padding: '8px 4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <img
                                src={isAway ? safeGame.awayLogo : safeGame.homeLogo}
                                alt={isAway ? safeGame.away : safeGame.home}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span>
                                {isAway
                                  ? (safeGame.awayNickname || safeGame.away)
                                  : (safeGame.homeNickname || safeGame.home)
                                }
                              </span>
                            </div>
                          </td>
                        {visibleBookmakers.map(book => {
                          const odds = spread[`spreads_${book}`];
                          const point = spread.pointsByBook?.[book] || spread.point;
                          return (
                            <td
                              key={`spreads-${book}-${index}`}
                              className={getCellClassName(safeGame.gameIDmlb, spread.playerId, 'spreads', book, point, 'spread')}
                              style={{ textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem', cursor: odds ? 'pointer' : 'default' }}
                              onClick={() => {
                                if (odds && onOddsClick) {
                                  onOddsClick(safeGame.gameIDmlb, spread.playerId, spread.name, 'spreads', book, odds, safeGame.away, safeGame.home, spread.team, null, point, 'spread', spread.team === safeGame.home ? safeGame.homeTeamId : safeGame.awayTeamId);
                                }
                              }}
                              onContextMenu={(e) => {
                                if (odds && onOddsContextMenu) {
                                  onOddsContextMenu(e, safeGame.gameIDmlb, spread.playerId, spread.name, 'spreads', book, odds, safeGame.away, safeGame.home, spread.team, null, point, 'spread');
                                }
                              }}
                            >
                              {odds ? (
                                <div>
                                  <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                    {point > 0 ? `+${point}` : point}
                                  </div>
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Mobile Book Pagination */}
            {totalPages > 1 && !selectedMarket.includes('first') && !selectedMarket.includes('scorer') && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '15px',
                marginTop: '10px'
              }}>
                <button
                  onClick={() => setMobileBookIndex(Math.max(0, mobileBookIndex - 1))}
                  disabled={mobileBookIndex === 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: mobileBookIndex === 0 ? 'default' : 'pointer',
                    opacity: mobileBookIndex === 0 ? 0.3 : 1,
                    padding: '5px'
                  }}
                >
                  ‹
                </button>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {[...Array(totalPages)].map((_, index) => (
                    <div
                      key={index}
                      onClick={() => setMobileBookIndex(index)}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: index === mobileBookIndex ? '#007bff' : '#dee2e6',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setMobileBookIndex(Math.min(totalPages - 1, mobileBookIndex + 1))}
                  disabled={mobileBookIndex === totalPages - 1}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: mobileBookIndex === totalPages - 1 ? 'default' : 'pointer',
                    opacity: mobileBookIndex === totalPages - 1 ? 0.3 : 1,
                    padding: '5px'
                  }}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Handle totals and spreads markets (similar to h2h but single table)
  if (selectedMarket === 'totals') {
    totalsData = filteredData.filter(p => p.playerId?.startsWith('totals_'));
  } else if (selectedMarket === 'spreads') {
    spreadsData = filteredData.filter(p => p.playerId?.startsWith('spreads_'));
  }

  // Helper function to get best odds from API data
  const findBestOdds = (player) => {
    // Use pre-calculated data from the API
    const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
    const bestBook = player[`${selectedMarket}_bestBook`];
    const bestBookDiff = player[`${selectedMarket}_bestBookDiff`];
    const bestBooks = player[`${selectedMarket}_bestBooks`] || [];

    // Count how many books have odds
    let bookCount = 0;
    allBookmakers.forEach(book => {
      const odds = player[`${selectedMarket}_${book}`];
      if (odds !== null && odds !== undefined) {
        bookCount++;
      }
    });

    return { bestOdds: bestBookPrice, bestBook, bestBookPrice, bestBookDiff, bookCount, bestBooks };
  };

  // Helper function to find best over and under odds for O/U markets
  const findBestOverUnder = (playerName, allPlayerRows) => {
    let bestOverOdds = null;
    let bestOverBook = null;
    let bestOverPoint = null;
    let bestOverBookPrice = null;
    let bestOverBookDiff = null;
    let bestUnderOdds = null;
    let bestUnderBook = null;
    let bestUnderPoint = null;
    let bestUnderBookPrice = null;
    let bestUnderBookDiff = null;

    // Get all rows for this player
    const playerRows = allPlayerRows.filter(p => p.name === playerName);

    // Debug logging for Stephen Curry
    if (playerName.includes('Curry')) {
      console.log('Curry rows:', playerRows.length);
      console.log('First row sample:', playerRows[0]);
    }

    // Check each row (each represents a different point value)
    playerRows.forEach(row => {
      const point = row.point;

      // Check each bookmaker for this row
      allBookmakers.forEach(book => {
        const overOdds = row[`${selectedMarket}_${book}_over`];
        const underOdds = row[`${selectedMarket}_${book}_under`];
        const isAlternate = row[`${selectedMarket}_${book}_isAlternate`];

        // Skip alternates in O/U view - only show mainlines
        if (isAlternate === true) {
          return;
        }

        // Debug logging for Curry + DraftKings
        if (playerName.includes('Curry') && book === 'draftkings' && overOdds) {
          console.log(`Curry DK point ${point}:`, {
            overOdds,
            underOdds,
            isAlternate,
            key: `${selectedMarket}_${book}_over`,
            rowKeys: Object.keys(row).filter(k => k.includes('draftkings'))
          });
        }

        if (overOdds !== null && overOdds !== undefined && point !== null && point !== undefined) {
          // Skip odds -200 or worse in edge view (only consider odds > -200)
          if (nbaViewMode === 'bestline' && overOdds <= -200) {
            return;
          }

          // For over: prefer lowest point value, then best odds
          if (!bestOverOdds ||
              point < bestOverPoint ||
              (point === bestOverPoint && overOdds > bestOverOdds)) {
            bestOverOdds = overOdds;
            bestOverBook = book;
            bestOverPoint = point;
            bestOverBookPrice = row[`${selectedMarket}_bestBookPrice`];
            bestOverBookDiff = row[`${selectedMarket}_bestBookDiff`];
          }
        }

        if (underOdds !== null && underOdds !== undefined && point !== null && point !== undefined) {
          // Skip odds -200 or worse in edge view (only consider odds > -200)
          if (nbaViewMode === 'bestline' && underOdds <= -200) {
            return;
          }

          // For under: prefer highest point value, then best odds
          if (!bestUnderOdds ||
              point > bestUnderPoint ||
              (point === bestUnderPoint && underOdds > bestUnderOdds)) {
            bestUnderOdds = underOdds;
            bestUnderBook = book;
            bestUnderPoint = point;
            bestUnderBookPrice = row[`${selectedMarket}_bestBookPrice`];
            bestUnderBookDiff = row[`${selectedMarket}_bestBookDiff`];
          }
        }
      });
    });

    if (playerName.includes('Curry')) {
      console.log('Curry best over:', { bestOverOdds, bestOverBook, bestOverPoint });
      console.log('Curry best under:', { bestUnderOdds, bestUnderBook, bestUnderPoint });
    }

    return {
      bestOverOdds,
      bestOverBook,
      bestOverPoint,
      bestOverBookPrice,
      bestOverBookDiff,
      bestUnderOdds,
      bestUnderBook,
      bestUnderPoint,
      bestUnderBookPrice,
      bestUnderBookDiff
    };
  };

  // Organize players by team
  const playersByTeam = {};

  sortedData.forEach(player => {
    let teamKey = 'unknown';
    let teamName = 'Unknown Team';
    let teamLogo = null;

    if (player.team === safeGame.away || (player.teamId && player.teamId === safeGame.awayTeamId)) {
      teamKey = 'away';
      teamName = safeGame.awayTeamFull || safeGame.away;
      teamLogo = safeGame.awayLogo || getTeamLogoUrl(safeGame.away);
    } else if (player.team === safeGame.home || (player.teamId && player.teamId === safeGame.homeTeamId)) {
      teamKey = 'home';
      teamName = safeGame.homeTeamFull || safeGame.home;
      teamLogo = safeGame.homeLogo || getTeamLogoUrl(safeGame.home);
    } else {
      teamKey = player.team || 'unknown';
      teamName = player.team || 'Unknown Team';
      teamLogo = player.team ? getTeamLogoUrl(player.team) : null;
    }

    if (!playersByTeam[teamKey]) {
      playersByTeam[teamKey] = {
        name: teamName,
        logo: teamLogo,
        players: []
      };
    }

    playersByTeam[teamKey].players.push(player);
  });

  return (
    <div className="table-group">
      <GameHeader
        gameTime={gameTime}
          gameDate={gameDate}
        gameStatus={gameStatus}
        game={game}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={true}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatBasketballLiveStatus}
        showScores={true}
            isLoading={showLoading}
      />

      {!isCollapsed && (
        <div>
          {/* Player Props Tables */}
          {Object.entries(playersByTeam).sort(([a], [b]) => {
            // Ensure away team comes before home team
            if (a === 'away') return -1;
            if (b === 'away') return 1;
            if (a === 'home') return 1;
            if (b === 'home') return -1;
            return a.localeCompare(b);
          }).map(([teamKey, teamData], teamIndex) => {
            // Check if this is an O/U market for header styling
            const isOUMarket = teamData.players.length > 0 && ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_blocks', 'player_steals', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);

            return (
            <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '16px' : '0' }}>
              <Table bordered size="sm" className="text-center">
                <thead>
                  <tr>
                    <th style={{ width: '40%', fontSize: '0.75rem' }}>NAME</th>
                    {nbaViewMode === 'bestline' && isOUMarket ? (
                      visibleBookmakers.map(book => {
                        const bookLink = game?.sgoLinks?.bookmakers?.[book];
                        return (
                          <th key={book} style={{ padding: '4px', textAlign: 'center' }}>
                            {bookLink ? (
                              <a
                                href={bookLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  touchAction: 'auto',
                                  WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                  padding: '4px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <img
                                  src={getBookLogoUrl(book)}
                                  alt={book}
                                  style={{
                                    height: '14px',
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                    display: 'block'
                                  }}
                                />
                              </a>
                            ) : (
                              <img
                                src={getBookLogoUrl(book)}
                                alt={book}
                                style={{ height: '14px' }}
                              />
                            )}
                          </th>
                        );
                      })
                    ) : (
                      <th colSpan={3} style={{ width: '60%', fontSize: '0.75rem' }}>BEST BOOKS</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let uniquePlayerIndex = 0;
                    let lastPlayerName = '';

                    return teamData.players.map((player, index) => {
                      const isFirstRowForPlayer = index === 0 || teamData.players[index - 1].name !== player.name;

                      // Check if this is an O/U market
                      const isOUMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_blocks', 'player_steals', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);

                      // Increment unique player index only when we encounter a new player
                      if (player.name !== lastPlayerName) {
                        if (lastPlayerName !== '') uniquePlayerIndex++;
                        lastPlayerName = player.name;
                      }

                      // Determine background color based on unique player index
                      const bgColor = (uniquePlayerIndex % 2 === 0) ? 'rgb(26, 26, 26)' : 'rgb(45, 45, 45)';

                      // For O/U markets, check view mode
                      if (isOUMarket && isFirstRowForPlayer) {
                        // Best Line view - show all books with best edge for each
                        if (nbaViewMode === 'bestline') {
                          // Get all lines for this player (all different point values)
                          const allPlayerLines = teamData.players.filter(p => p.name === player.name);

                          return (
                            <React.Fragment key={player.tierKey || player.id || `${player.name}-${index}`}>
                              <tr style={{ backgroundColor: bgColor }}>
                                <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <img
                                      src={teamData.logo}
                                      alt={teamKey}
                                      style={{ width: '14px', height: '14px' }}
                                      onError={(e) => {
                                        e.target.style.visibility = 'hidden';
                                        e.target.style.width = '0px';
                                      }}
                                    />
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onContextMenu={handleNameContextMenu(player)}
                                      onClick={() => {
                                        if (onPlayerClick) {
                                          onPlayerClick(
                                            safeGame.gameIDmlb,
                                            player.playerId,
                                            player.name,
                                            selectedMarket,
                                            safeGame.away,
                                            safeGame.home,
                                            player.team || player.teamId,
                                            player.positionCode || player.position
                                          );
                                        }
                                      }}
                                    >
                                      {formatPlayerName(player.name, player.starter, 'wnba')}
                                    </span>
                                    <PositionBadge player={player} game={safeGame} />
                                  </div>
                                </td>
                                {visibleBookmakers.map(book => {
                                  // Pass all lines for this player to find the best edge
                                  const bestLine = getBestLineByEdge(allPlayerLines, book, selectedMarket);
                                  if (!bestLine) {
                                    return (
                                      <td key={book} style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem' }}>
                                        <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>-</span>
                                      </td>
                                    );
                                  }

                                  const { odds, point, side, bestBookPrice, bestBookDiff, isAvailable } = bestLine;

                                  // Validate that we have valid odds
                                  if (odds === null || odds === undefined) {
                                    return (
                                      <td key={book} style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem' }}>
                                        <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>-</span>
                                      </td>
                                    );
                                  }

                                  // Apply tier styling using the edge data from API
                                  const baseCellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, visibleBookmakers.length);

                                  return (
                                    <td
                                      key={book}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, point, side)}
                                      style={{ padding: '2px', textAlign: 'center', fontSize: '0.7rem', cursor: 'pointer', opacity: isAvailable === false ? 0.25 : undefined }}
                                      onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, point, side)}
                                      onContextMenu={(e) => onOddsContextMenu && onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, point, side)}
                                    >
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', ...baseCellStyle, padding: '4px', borderRadius: '3px' }}>
                                        <span style={{ fontSize: '0.65rem' }}>
                                          {side === 'over' ? `o${point}` : `u${point}`}
                                        </span>
                                        <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                          {odds > 0 ? `+${odds}` : odds}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            </React.Fragment>
                          );
                        }

                        // O/U view - show best over/under books
                        const {
                          bestOverOdds,
                          bestOverBook,
                          bestOverPoint,
                          bestOverBookPrice,
                          bestOverBookDiff,
                          bestUnderOdds,
                          bestUnderBook,
                          bestUnderPoint,
                          bestUnderBookPrice,
                          bestUnderBookDiff
                        } = findBestOverUnder(player.name, teamData.players);

                        return (
                          <React.Fragment key={player.tierKey || player.id || `${player.name}-${index}`}>
                            {/* First row: Player info and best over */}
                            <tr style={{ backgroundColor: bgColor }}>
                              <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem', borderBottom: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <img
                                    src={teamData.logo}
                                    alt={teamKey}
                                    style={{ width: '14px', height: '14px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                    }}
                                  />
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onContextMenu={handleNameContextMenu(player)}
                                    onClick={() => {
                                      if (onPlayerClick) {
                                        onPlayerClick(
                                          safeGame.gameIDmlb,
                                          player.playerId,
                                          player.name,
                                          selectedMarket,
                                          safeGame.away,
                                          safeGame.home,
                                          player.team || player.teamId,
                                          player.positionCode || player.position
                                        );
                                      }
                                    }}
                                  >
                                    {formatPlayerName(player.name, player.starter, 'wnba')}
                                  </span>
                                  <PositionBadge player={player} game={safeGame} />
                                </div>
                              </td>

                              <td style={{ padding: '2px', textAlign: 'center', borderBottom: 'none' }}>
                                {bestOverBook && (() => {
                                  const bookLink = game?.sgoLinks?.bookmakers?.[bestOverBook];
                                  return bookLink ? (
                                    <a
                                      href={bookLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: 'inline-block',
                                        touchAction: 'auto',
                                        WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                        padding: '2px'
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      onTouchEnd={(e) => e.stopPropagation()}
                                    >
                                      <img
                                        src={getBookLogoUrl(bestOverBook)}
                                        alt={bestOverBook}
                                        style={{
                                          height: '14px',
                                          cursor: 'pointer',
                                          pointerEvents: 'auto',
                                          display: 'block'
                                        }}
                                      />
                                    </a>
                                  ) : (
                                    <img
                                      src={getBookLogoUrl(bestOverBook)}
                                      alt={bestOverBook}
                                      style={{ height: '14px' }}
                                    />
                                  );
                                })()}
                              </td>

                              {(() => {
                                const baseCellStyle = bestOverOdds ? getCellStyle(bestOverOdds, bestOverBookPrice, bestOverBookDiff, allBookmakers.length) : {};
                                return (
                                  <>
                                    <td style={{ padding: '2px 2px 0px 2px', textAlign: 'center', fontSize: '0.7rem', borderBottom: 'none', ...baseCellStyle }}>
                                      {bestOverPoint !== null && bestOverPoint !== 'yes' && bestOverPoint !== 'no' && (
                                        <span style={{ fontWeight: 'bold' }}>
                                          O {bestOverPoint}
                                        </span>
                                      )}
                                    </td>

                                    <td
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestOverBook, bestOverPoint, 'over')}
                                      style={{ padding: '2px 2px 0px 2px', textAlign: 'center', fontSize: '0.7rem', borderBottom: 'none', cursor: bestOverOdds ? 'pointer' : 'default', ...baseCellStyle }}
                                      onClick={() => {
                                        if (onOddsClick && bestOverOdds) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestOverBook, bestOverOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, bestOverPoint, 'over');
                                        }
                                      }}
                                      onContextMenu={(e) => {
                                        if (onOddsContextMenu && bestOverOdds) {
                                          onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestOverBook, bestOverOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, bestOverPoint, 'over');
                                        }
                                      }}
                                    >
                                      {bestOverOdds && (
                                        <span style={{ fontWeight: 'bold' }}>
                                          {bestOverOdds > 0 ? `+${bestOverOdds}` : bestOverOdds}
                                        </span>
                                      )}
                                    </td>
                                  </>
                                );
                              })()}
                            </tr>

                            {/* Second row: Best under */}
                            {bestUnderOdds && (
                              <tr style={{ backgroundColor: bgColor }}>
                                <td style={{ padding: '6px', borderTop: 'none' }}></td>

                                <td style={{ padding: '2px', textAlign: 'center', borderTop: 'none' }}>
                                  {bestUnderBook && (() => {
                                    const bookLink = game?.sgoLinks?.bookmakers?.[bestUnderBook];
                                    return bookLink ? (
                                      <a
                                        href={bookLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          display: 'inline-block',
                                          touchAction: 'auto',
                                          WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                          padding: '2px'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onTouchEnd={(e) => e.stopPropagation()}
                                      >
                                        <img
                                          src={getBookLogoUrl(bestUnderBook)}
                                          alt={bestUnderBook}
                                          style={{
                                            height: '14px',
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            display: 'block'
                                          }}
                                        />
                                      </a>
                                    ) : (
                                      <img
                                        src={getBookLogoUrl(bestUnderBook)}
                                        alt={bestUnderBook}
                                        style={{ height: '14px' }}
                                      />
                                    );
                                  })()}
                                </td>

                                {(() => {
                                  const baseCellStyle = getCellStyle(bestUnderOdds, bestUnderBookPrice, bestUnderBookDiff, allBookmakers.length);
                                  return (
                                    <>
                                      <td style={{ padding: '0px 2px 2px 2px', textAlign: 'center', fontSize: '0.7rem', borderTop: 'none', ...baseCellStyle }}>
                                        {bestUnderPoint !== 'yes' && bestUnderPoint !== 'no' && (
                                          <span style={{ fontWeight: 'bold' }}>
                                            U {bestUnderPoint}
                                          </span>
                                        )}
                                      </td>

                                      <td
                                        className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestUnderBook, bestUnderPoint, 'under')}
                                        style={{ padding: '0px 2px 2px 2px', textAlign: 'center', fontSize: '0.7rem', borderTop: 'none', cursor: 'pointer', ...baseCellStyle }}
                                        onClick={() => {
                                          if (onOddsClick) {
                                            onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestUnderBook, bestUnderOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, bestUnderPoint, 'under');
                                          }
                                        }}
                                        onContextMenu={(e) => {
                                          if (onOddsContextMenu) {
                                            onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestUnderBook, bestUnderOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, bestUnderPoint, 'under');
                                          }
                                        }}
                                      >
                                        <span style={{ fontWeight: 'bold' }}>
                                          {bestUnderOdds > 0 ? `+${bestUnderOdds}` : bestUnderOdds}
                                        </span>
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      }

                      // For non-O/U markets or non-first rows, use original single row display
                      if (isOUMarket && !isFirstRowForPlayer) {
                        return null; // Skip additional rows for O/U markets
                      }

                      const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount, bestBooks } = findBestOdds(player);

                      return (
                        <tr key={player.tierKey || player.id || `${player.name}-${index}`} style={{ backgroundColor: bgColor }}>
                          <td style={{ textAlign: 'left', padding: '6px', fontSize: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {isFirstRowForPlayer && (
                                  <>
                                    <img
                                      src={teamData.logo}
                                      alt={teamKey}
                                      style={{ width: '14px', height: '14px' }}
                                      onError={(e) => {
                                        e.target.style.visibility = 'hidden';
                                        e.target.style.width = '0px';
                                      }}
                                    />
                                    <span
                                      style={{ cursor: 'pointer' }}
                                      onContextMenu={handleNameContextMenu(player)}
                                      onClick={() => {
                                        if (onPlayerClick) {
                                          onPlayerClick(
                                            safeGame.gameIDmlb,
                                            player.playerId,
                                            player.name,
                                            selectedMarket,
                                            safeGame.away,
                                            safeGame.home,
                                            player.team || player.teamId,
                                            player.positionCode || player.position
                                          );
                                        }
                                      }}
                                    >
                                      {formatPlayerName(player.name, player.starter, 'wnba')}
                                    </span>
                                    <PositionBadge player={player} game={safeGame} />
                                  </>
                                )}
                              </div>
                              {player.point !== null && player.point !== undefined && (
                                <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                  {player.point === 'yes' ? 'Yes' :
                                   player.point === 'no' ? 'No' :
                                   `Over ${player.point}`}
                                </span>
                              )}
                            </div>
                          </td>
                          {bestOdds !== null && bestBook ? (
                            <>
                              <td style={{ padding: '4px', textAlign: 'center', width: '60px' }}>
                                {(() => {
                                  // Handle multi-book case
                                  if (bestBook === 'multi') {
                                    if (bestBooks.length === 2) {
                                      return (
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                          {bestBooks.map(book => (
                                            <img
                                              key={book}
                                              src={getBookLogoUrl(book)}
                                              alt={book}
                                              style={{ height: '14px' }}
                                            />
                                          ))}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                          {bestBooks.length} books
                                        </span>
                                      );
                                    }
                                  }

                                  const bookLink = game?.sgoLinks?.bookmakers?.[bestBook];
                                  return bookLink ? (
                                    <a
                                      href={bookLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: 'inline-block',
                                        touchAction: 'auto',
                                        WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                        padding: '2px'
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      onTouchEnd={(e) => e.stopPropagation()}
                                    >
                                      <img
                                        src={getBookLogoUrl(bestBook)}
                                        alt={bestBook}
                                        style={{
                                          height: '16px',
                                          cursor: 'pointer',
                                          pointerEvents: 'auto',
                                          display: 'block'
                                        }}
                                      />
                                    </a>
                                  ) : (
                                    <img
                                      src={getBookLogoUrl(bestBook)}
                                      alt={bestBook}
                                      style={{ height: '16px' }}
                                    />
                                  );
                                })()}
                              </td>
                              <td
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestBook, null, 'yes')}
                                style={{ padding: '6px', textAlign: 'center', cursor: 'pointer' }}
                                onClick={() => {
                                  if (onOddsClick) {
                                    onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestBook, bestOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, null, 'yes');
                                  }
                                }}
                                onContextMenu={(e) => {
                                  if (onOddsContextMenu) {
                                    onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, bestBook, bestOdds, safeGame.away, safeGame.home, player.team, player.positionCode || player.position, null, 'yes');
                                  }
                                }}
                              >
                                <span style={getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount)}>
                                  {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '4px' }}></td>
                              <td style={{ padding: '6px' }}></td>
                            </>
                          )}
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </Table>
            </div>
            );
          })}

          {/* Mobile Book Pagination for Player Props */}
          {totalPages > 1 && !selectedMarket.includes('first') && !selectedMarket.includes('scorer') && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px',
              marginTop: '10px',
              marginBottom: '10px'
            }}>
              <button
                onClick={() => setMobileBookIndex(Math.max(0, mobileBookIndex - 1))}
                disabled={mobileBookIndex === 0}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: mobileBookIndex === 0 ? 'default' : 'pointer',
                  opacity: mobileBookIndex === 0 ? 0.3 : 1,
                  padding: '5px'
                }}
              >
                ‹
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                {[...Array(totalPages)].map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setMobileBookIndex(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: index === mobileBookIndex ? '#007bff' : '#dee2e6',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                  />
                ))}
              </div>

              <button
                onClick={() => setMobileBookIndex(Math.min(totalPages - 1, mobileBookIndex + 1))}
                disabled={mobileBookIndex === totalPages - 1}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: mobileBookIndex === totalPages - 1 ? 'default' : 'pointer',
                  opacity: mobileBookIndex === totalPages - 1 ? 0.3 : 1,
                  padding: '5px'
                }}
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileBasketballDataTable;